/**
 * Webhook Delivery Log API Service (Sprint C14 Lane B WP B-4)
 *
 * Type-safe access to the tenant-scoped webhook delivery log endpoints.
 * Types derived from webhook-delivery-log.openapi.yaml -- no manual interfaces.
 *
 * Endpoints consumed (canonical /api/admin/webhook-deliveries spec):
 *   GET /webhook-deliveries          - listWebhookDeliveries (cursor-paginated)
 *   GET /webhook-deliveries/health   - getWebhookHealth (24h aggregate)
 *   GET /webhook-deliveries/{id}     - getWebhookDelivery (full detail + retry chain)
 */

import { webhookDeliveryLogClient } from '../client';
import type { components, paths } from '@aaimplatform/institutional-types/webhook-delivery-log';

// Re-export generated types for page-level consumers.
export type WebhookDeliveryStatus = components['schemas']['WebhookDeliveryStatus'];
export type WebhookDeliverySummary = components['schemas']['WebhookDeliverySummary'];
export type WebhookDeliveryDetail = components['schemas']['WebhookDeliveryDetail'];
export type WebhookRetryChainEntry = components['schemas']['WebhookRetryChainEntry'];
export type WebhookDeliveryListResponse = components['schemas']['WebhookDeliveryListResponse'];
export type WebhookHealthMetrics = components['schemas']['WebhookHealthMetrics'];

// The listWebhookDeliveries query parameters, from the generated operation.
// It was a hand-written interface whose `eventType?: string` admitted any
// string; the spec types the parameter as one WebhookEventType (fi-30).
export type WebhookDeliveryListQuery = NonNullable<paths['/webhook-deliveries']['get']['parameters']['query']>;

export const webhookDeliveryLogApi = {
  /**
   * List webhook deliveries for the caller's tenant. Cursor-paginated.
   */
  async list(query: WebhookDeliveryListQuery = {}): Promise<WebhookDeliveryListResponse> {
    const { data, error } = await webhookDeliveryLogClient.GET('/webhook-deliveries', {
      params: {
        query: {
          cursor: query.cursor,
          limit: query.limit,
          status: query.status,
          eventType: query.eventType,
          since: query.since,
        },
      },
    });
    if (error) {
      throw new Error('Failed to fetch webhook deliveries');
    }
    return data;
  },

  /**
   * Get the full delivery detail for a single record (includes retry chain).
   */
  async getById(id: string): Promise<WebhookDeliveryDetail> {
    const { data, error } = await webhookDeliveryLogClient.GET('/webhook-deliveries/{id}', {
      params: { path: { id } },
    });
    if (error) {
      throw new Error('Failed to fetch webhook delivery');
    }
    return data;
  },

  /**
   * 24-hour aggregate health metrics for the caller's tenant.
   */
  async getHealth(): Promise<WebhookHealthMetrics> {
    const { data, error } = await webhookDeliveryLogClient.GET('/webhook-deliveries/health');
    if (error) {
      throw new Error('Failed to fetch webhook delivery health');
    }
    return data;
  },
};
